/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { CURRENT_TCAS_YEAR } from 'constants/school-year';
import cn from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import { callAPI, getRoundState, isCitizenId, isGNumber, userErrorMessage, showErrorToast, thaiMoment } from 'helpers';

// Components
import Search from './Search';
import FavoriteItem from './FavoriteItem';
import AdmissionEdit from './AdmissionEdit';
import AdmissionConfirm from './AdmissionConfirm';
import AdmissionApplied from './AdmissionApplied';
import AdmissionLogs from './AdmissionLogs';
import ModalChooseRound3 from 'components/profile/enrollments/round3/ModalChooseRound3';
import ModalJustSetZero from 'components/profile/enrollments/round3/ModalJustSetZero';
import ModalPassportStudent from 'components/profile/enrollments/round3/ModalPassportStudent';
import ModalRedeemedStudent from 'components/profile/enrollments/round3/ModalRedeemedStudent';
// eslint-disable-next-line no-unused-vars
import RoundContent from '../RoundContent';
import LoadError from 'components/LoadError';

// Hooks
import { useSelector } from 'react-redux';
import useFavorites from 'hooks/round3/useFavorites';
import useAdmission from 'hooks/round3/useAdmission';
import { useNavigate } from 'react-router-dom';

// Data
import enabled from 'data/enabled.json';
import { LATE_FEE, round3ApplyPhase, round3QuotaUsed } from 'late-fee';

const roundType = `3_${CURRENT_TCAS_YEAR}`;
// code จาก getStudentStatus ที่หมดสิทธิ์สมัครรอบ 3 (4 ยืนยันสิทธิ์ค้าง, 8 สละครบ 2 ครั้ง, 13-15/20-22 disqualify)
// server มี gate ชุดเดียวกันใน apply-round3 (tcas65) — ที่นี่แค่ UX
const BLOCKED_STATUS_CODES = [4, 8, 13, 14, 15, 20, 21, 22];
// ช่วงรับสมัครจาก enabled.round3.apply_* — เช่น "รับสมัคร 7 - 13 พ.ค. 70" (ไม่ hardcode วันรายปีอีก)
const APPLY_RANGE_TEXT = `รับสมัคร ${thaiMoment(enabled.round3.apply_start).format('D')} - ${thaiMoment(enabled.round3.apply_end).locale('th').format('D MMM')} ${(thaiMoment(enabled.round3.apply_end).year() + 543) % 100}`;
// ช่วงสมัครล่าช้า (ทปอ. 17 ต.ค. 69) late_start → apply_end — ค่าสมัครเดิม + 200 บาทต่อการยืนยัน
const LATE_RANGE_TEXT = `สมัครล่าช้า ${thaiMoment(enabled.round3.late_start).format('D')} - ${thaiMoment(enabled.round3.apply_end).locale('th').format('D MMM')} ${(thaiMoment(enabled.round3.apply_end).year() + 543) % 100} (+${LATE_FEE} บาท)`;
const Round3 = () => {
	const navigate = useNavigate();

	const { loading, loadError, reload, favorites, mutationBusy, onSavePriorities, studentStatus = {}, isApplicable, alreadySetZero, redeemableAmount, redeemedAmount, onAdd, onRemove, onSelect, onDragEnd, onRemovePriority } = useFavorites();
	const { projects, availables } = useAdmission(favorites, studentStatus);
	const profile = useSelector((state) => _.get(state, 'app.user') || {});
	const [active, setActive] = useState(false);
	const [editable, setEditable] = useState(1);
	const [modalVisible, setModalVisible] = useState(false);

	const [favoriteActive, setFavoriteActive] = useState(true);
	const [editActive, setEditActive] = useState(true);
	const [admissionLoading, setAdmissionLoading] = useState(false);
	const [loadFailed, setLoadFailed] = useState(false);
	const [admissions, setAdmissions] = useState([]);
	const [offerProjects, setOfferProjects] = useState([]);
	const [logs, setLogs] = useState([]);
	const [timestamp, setTimestamp] = useState();
	const [isExpired, setExpired] = useState(false);
	const [justSetZero, setJustSetZero] = useState(false);
	const [passportVisible, setPassportVisible] = useState(false);
	const [redeemedVisible, setRedeemedVisible] = useState(false);

	const { flow } = getRoundState({ roundType, timestamp });
	// 'normal' | 'late' (สมัครล่าช้า +200) | 'closed' — src/late-fee.js
	const applyPhase = timestamp ? round3ApplyPhase(timestamp) : null;

	useEffect(() => {
		let cancelled = false;
		const fetchAdmissions = async () => {
			try {
				setAdmissionLoading(true);
				const { result, offer_projects, timestamp } = await callAPI({ url: '/applicants/me/admissions' });
				if (cancelled) return;
				setLogs(_.reverse(_.cloneDeep(result)));

				const admissions = result.filter(({ set_zero, rd_rollback, sz_rollback }) => !set_zero || rd_rollback || sz_rollback);
				setJustSetZero(_.get(_.last(result), 'set_zero'));
				setAdmissions(admissions);
				setAdmissionLoading(false);
				setOfferProjects(offer_projects);
				setFavoriteActive(_active => admissions.length ? false : _active);
				setEditable(admissions.length ? 0 : 1);
				setTimestamp(timestamp);
				setExpired(moment(timestamp).isAfter(enabled.round3.apply_end));
				if (getRoundState({ roundType, timestamp }).flow !== 'not-open') setActive(true);
			} catch (error) {
				console.log({ error });
				if (cancelled) return;
				setLoadFailed(true);
				setAdmissionLoading(false);
			}
		}

		fetchAdmissions();
		return () => { cancelled = true; };
	}, []);

	useEffect(() => {
		if (!studentStatus.code || BLOCKED_STATUS_CODES.includes(studentStatus.code) || isExpired) {

		} else if (!isCitizenId(profile.citizen_id) && !isGNumber(profile.citizen_id)) {
			if (!_.get(profile, `consents_accepted_at.passport_round3_${CURRENT_TCAS_YEAR}`)) {
				setPassportVisible(true);
			}
		} else if (_.get(profile, 'r3_redeemed') && !_.get(profile, `consents_accepted_at.redeemed_round3_${CURRENT_TCAS_YEAR}`)) {
			setRedeemedVisible(true);
		} else if (profile.r3_redeemable && timestamp && moment(timestamp).isBetween(enabled.round3.apply_start, enabled.round3.apply_end)) {
			navigate('/profile/redeem-round3');
		}
	}, [profile, studentStatus, isExpired, timestamp, navigate]);

	const [accepting, setAccepting] = useState(false);
	const _onAcceptConsent = async (consentType) => {
		if (accepting) return;
		try {
			setAccepting(true);
			await callAPI({
				method: 'POST',
				url: '/applicants/me/consents',
				body: {
					consentType,
				},
			});
			setAccepting(false);
		} catch (error) {
			console.error(`Error while trying to accept consent: ${error.message}`);
			showErrorToast(userErrorMessage(error));
			setAccepting(false);
		}
		setPassportVisible(false);
		setRedeemedVisible(false);
	};

	const currentApplyId = logs.length
		? (_.get(_.maxBy(logs.filter(({ set_zero }) => set_zero), 'apply_id'), 'apply_id') || 0) + 1
		: 1;

	if (flow === 'not-open') {
		return (
			<div className={cn('r-3', { active })}>
				<header onClick={() => setActive((_active) => !_active)}>
					<b>
						<small>{'รอบ'}</small>3
					</b>
					<h3>
						Admission
						<small>{APPLY_RANGE_TEXT}</small>
					</h3>
					<i className="i-down" />
				</header>
				{/* <div className="content -announce">
					<div>
						<h3>{'ทดลองจัดอันดับสาขาวิชาที่สนใจ'}</h3>
						<h4 className='color-orange' style={{ fontSize: 18 }}>
							<div>{'*การทดลองจัดอันดับนี้ อ้างอิงจากเกณฑ์ล่าสุดที่แต่ละสถาบันนำเข้าระบบ หากพบปัญหาในการจัดอันดับ'}</div>
							<div>{'สามารถติดต่อสถาบันนั้น ๆ หรือติดต่อทปอ. ให้ช่วยประสานงานทางเฟซบุ๊ก '}<u>Mytcas.com</u></div>
						</h4>
						<p>
							<div>{'สามารถค้นหาและจัดอันดับสาขาวิชาที่สนใจ'}</div>
							<div>{'เพื่อใช้ในการสมัครรอบ 3 Admission'}</div>
						</p>
					</div>
					<Link to='/profile/favorite-round3' className={cn('btn-icon cursor-pointer')}>
						<b className="text">{'ทดลองจัดอันดับ'}</b>
					</Link>
				</div> */}
				<RoundContent
					flow={flow}
					roundType={roundType}
					loading={loading}
					timestamp={timestamp} />
			</div>
		);
	}

	return (
		<div className={cn('r-3', { active })}>
			<header onClick={() => setActive((_active) => !_active)}>
				<b>
					<small>{'รอบ'}</small>3
				</b>
				<h3>
					Admission
					<small>{(isExpired ? 'หมดเขตการสมัครแล้ว' : applyPhase === 'late' ? LATE_RANGE_TEXT : APPLY_RANGE_TEXT)}</small>
				</h3>
				<i className="i-down" />
			</header>
			{(loading || admissionLoading)
				? null
				: loadFailed
					? <LoadError />
					: loadError
					? <LoadError onRetry={reload} />
					: BLOCKED_STATUS_CODES.includes(studentStatus.code)
					? (
						<div className="content">
							<div>
								<h3>{'ท่านไม่มีสิทธิ์สมัครในรอบนี้'}</h3>
								{BLOCKED_STATUS_CODES.includes(studentStatus.code) && <p>{studentStatus.message.split(' --> ')[0]}</p>}
							</div>
						</div>
					) : isExpired ? (
						<div className="content">
							<AdmissionApplied
								logs={logs}
								isExpired
								alreadySetZero={alreadySetZero}
								redeemableAmount={redeemableAmount}
								redeemedAmount={redeemedAmount}
								studentStatus={studentStatus}
								admissions={admissions}
								offerProjects={offerProjects} />
							<AdmissionLogs logs={logs} />
						</div>
					) : (
						<div className="content">
							<div className={cn('t-collapse', { active: favoriteActive })}>
								<h3 className="r-3-bar t-toggle" onClick={() => setFavoriteActive(_active => !_active)}>
									<i className="i-fav" />
									{'สาขาวิชาที่สนใจ'}{favorites.length ? ` (${favorites.length}/20)` : ''}
									<i className="i-toggle i-down-teal" />
								</h3>
								<div className="t-content">
									<ul className="r-choose -fav">
										{(favorites || []).map((data, index) =>
											<FavoriteItem
												key={data._id}
												index={index}
												data={data}
												favorites={favorites}
												studentStatus={studentStatus}
												onRemove={onRemove} />)}
									</ul>
									<Search
										profile={profile}
										favorites={favorites}
										studentStatus={studentStatus}
										onAddToFavorites={onAdd} />
								</div>
							</div>
							{(admissions.length && !editable)
								? <AdmissionApplied
									logs={logs}
									alreadySetZero={alreadySetZero}
									redeemableAmount={redeemableAmount}
								redeemedAmount={redeemedAmount}
									studentStatus={studentStatus}
									admissions={admissions}
									phase={applyPhase}
									onEdit={() => setEditable(1)} />
								: (isApplicable ? (
									<div className={cn("t-collapse", { active: editActive })}>
										<h3 className="r-3-bar t-toggle" onClick={() => setEditActive(_active => !_active)}>
											<i className="i-apply" />
											{'สาขาวิชาที่สมัคร'}{(projects || []).length ? ` (${(projects || []).length}/10)` : ''}
											<i className="i-toggle i-down-teal" />
										</h3>
										<div className="t-content">
											<h4><b>{'การสมัครครั้งที่'} {currentApplyId}</b></h4>
											{/* _priority = อันดับที่ server บันทึกไว้ — draft ถอดออกหมดแล้วต้องยังเห็นปุ่มบันทึก เพื่อเซฟรายการว่างล้างอันดับใน server */}
											{favorites.some(({ priority, _priority }) => priority || _priority) ? (
												<>
													{editable === 1 ? (
														<AdmissionEdit
															favorites={favorites}
															studentStatus={studentStatus}
															redeemableAmount={redeemableAmount}
								redeemedAmount={redeemedAmount}
															onModalOpen={() => setModalVisible(true)}
															onDragEnd={onDragEnd}
															onRemovePriority={onRemovePriority}
															mutationBusy={mutationBusy}
															onSavePriorities={onSavePriorities}
															onSaveSuccess={(saved) => setEditable(saved.favorites.some(({ priority }) => priority != null) ? 2 : 1)}
															phase={applyPhase}
															quotaUsed={round3QuotaUsed(admissions, applyPhase)}
															onBack={() => setEditable(0)} />
													) : editable === 2 ? (
														<AdmissionConfirm
															admissions={admissions}
															favorites={favorites}
															studentStatus={studentStatus}
															alreadySetZero={alreadySetZero}
															redeemableAmount={redeemableAmount}
								redeemedAmount={redeemedAmount}
															onEdit={() => setEditable(1)}
															phase={applyPhase}
															quotaUsed={round3QuotaUsed(admissions, applyPhase)}
															onBack={() => setEditable(0)} />
													) : null}
												</>
											) : (
												<>
													<p>{'กรุณาเลือกสาขาวิชาที่จะสมัคร เรียงตามความสนใจ สามารถเลือกได้สูงสุด 10 อันดับ'}</p>
													{(availables || []).length ? (
														<a className="btn-add cursor-pointer disabled" onClick={() => { if (!mutationBusy) { setEditable(1); setModalVisible(true); } }}>
															<i className="i-add" />
															{'เลือกสาขาวิชาอันดับ'} 1
														</a>
													) : (
														<h4 className="color-teal">{'กรุณาเลือกสาขาวิชาที่สนใจ'}</h4>
													)}
												</>
											)}
										</div>
									</div>
								) : null)}
							<AdmissionLogs logs={logs} />
						</div>
					)
			}
			<ModalChooseRound3
				favorites={favorites}
				studentStatus={studentStatus}
				visible={modalVisible}
				onSelect={onSelect}
				onClose={() => setModalVisible(false)} />
			<ModalJustSetZero
				visible={justSetZero}
				onClick={() => setJustSetZero(false)} />
			<ModalPassportStudent
				visible={passportVisible}
				onClick={() => _onAcceptConsent(`passport_round3_${CURRENT_TCAS_YEAR}`)} />
			<ModalRedeemedStudent
				visible={redeemedVisible}
				onClick={() => _onAcceptConsent(`redeemed_round3_${CURRENT_TCAS_YEAR}`)} />
		</div >
	);
};

export default Round3;
